// frontend/src/pages/auth/ResetPassword.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import mayfairLogo from '../../logo.svg';
import api from '../../services/api';

// ─────────────────────────────────────────────────────────────
// Password requirements — must match backend
// ─────────────────────────────────────────────────────────────
const PASSWORD_MIN_LENGTH = 8;

const checkPassword = (password) => ({
    length: password.length >= PASSWORD_MIN_LENGTH,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    digit: /\d/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
});

const isPasswordValid = (password) => {
    const checks = checkPassword(password);
    return Object.values(checks).every(Boolean);
};

export default function ResetPassword() {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [token, setToken] = useState('');
    const [tokenValid, setTokenValid] = useState(false);
    const [verifying, setVerifying] = useState(true);

    const navigate = useNavigate();
    const location = useLocation();

    // Guard against React StrictMode double-invocation
    // and against the useEffect running on every render.
    const hasVerifiedRef = useRef(false);

    useEffect(() => {
        if (hasVerifiedRef.current) return;
        hasVerifiedRef.current = true;

        // Extract token once, from the URL that mounted this page.
        // Do NOT re-verify when location reference changes.
        const params = new URLSearchParams(location.search);
        const tokenParam = params.get('token');

        if (!tokenParam) {
            setError('Invalid or missing reset token.');
            setVerifying(false);
            return;
        }

        setToken(tokenParam);
        verifyToken(tokenParam);
        
    }, []); // run ONCE

    const verifyToken = async (tokenParam) => {
        try {
            // Encode the token — critical if it ever contains +, &, #, etc.
            const { data } = await api.get(
                `/auth/verify-reset-token?token=${encodeURIComponent(tokenParam)}`
            );

            if (data.valid) {
                setTokenValid(true);
                setError('');
            } else {
                setError(data.error || 'Invalid or expired reset token.');
            }
        } catch (err) {
            console.error('[ResetPassword] Token verification failed:', err);
            const status = err.response?.status;
            const code = err.response?.data?.code;
            const message = err.response?.data?.error;

            // Map backend codes to friendly messages
            let friendly = 'Invalid or expired reset token.';
            if (code === 'TOKEN_EXPIRED') {
                friendly = 'This reset link has expired. Please request a new one.';
            } else if (code === 'TOKEN_NOT_FOUND') {
                friendly = 'This reset link is invalid. It may have already been used.';
            } else if (code === 'MISSING_TOKEN') {
                friendly = 'The reset token is missing. Please open the link from your email.';
            } else if (message) {
                friendly = message;
            }

            setError(friendly);
        } finally {
            setVerifying(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Frontend validation — must match backend
        if (!password || !confirmPassword) {
            setError('Please fill in all fields.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        if (!isPasswordValid(password)) {
            setError('Password does not meet all requirements below.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            await api.post('/auth/reset-password', {
                token,
                password,
                confirmPassword,
            });

            setSuccess(true);
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            console.error('[ResetPassword] Error:', err);
            setError(
                err.response?.data?.error ||
                'Failed to reset password. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    };

    // ─────────────────────────────────────────────────────────
    // Loading state
    // ─────────────────────────────────────────────────────────
    if (verifying) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#3a663f]/10 mb-4">
                        <svg className="animate-spin h-8 w-8 text-[#3a663f]" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    </div>
                    <p className="text-gray-600">Verifying your reset link...</p>
                </div>
            </div>
        );
    }

    const checks = checkPassword(password);
    const passwordIsValid = isPasswordValid(password);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2d5232] to-[#3a663f] shadow-2xl shadow-[#3a663f]/25 p-3">
                        <img src={mayfairLogo} alt="Mayfair Insurance" className="w-full h-full object-contain" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mt-4">Set New Password</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Enter your new password below.
                    </p>
                </div>

                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100/80 p-8 md:p-10">
                    {success ? (
                        // ─── Success state ─────────────────────────
                        <div className="text-center space-y-4 animate-fadeIn">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100">
                                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Password Reset Successful!</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    Your password has been reset successfully.
                                </p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Redirecting to login...
                                </p>
                            </div>
                            <Link
                                to="/login"
                                className="block w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3 rounded-2xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 text-center"
                            >
                                Back to Login
                            </Link>
                        </div>
                    ) : tokenValid ? (
                        // ─── Valid token — password form ──────────
                        <form onSubmit={handleSubmit} className="space-y-5">
                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl flex items-start gap-3 animate-fadeIn shadow-sm">
                                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                                        <svg className="w-3 h-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                    <span className="text-sm flex-1">{error}</span>
                                    <button
                                        type="button"
                                        onClick={() => setError('')}
                                        className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    New Password
                                </label>
                                <div className="relative">
                                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center text-gray-400">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => {
                                            setPassword(e.target.value);
                                            if (error) setError('');
                                        }}
                                        placeholder="Enter new password"
                                        className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-[#3a663f]/10 focus:border-[#3a663f] outline-none text-gray-700 placeholder-gray-400 transition-all duration-200"
                                        autoComplete="new-password"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                    Confirm Password
                                </label>
                                <div className="relative">
                                    <div className="absolute left-0 top-0 bottom-0 w-12 flex items-center justify-center text-gray-400">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                        </svg>
                                    </div>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={confirmPassword}
                                        onChange={(e) => {
                                            setConfirmPassword(e.target.value);
                                            if (error) setError('');
                                        }}
                                        placeholder="Confirm new password"
                                        className="w-full pl-12 pr-12 py-3.5 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-[#3a663f]/10 focus:border-[#3a663f] outline-none text-gray-700 placeholder-gray-400 transition-all duration-200"
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
                                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    >
                                        {showPassword ? (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M21 12a9.97 9.97 0 01-1.543 2.971M3 3l18 18" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Live password requirements */}
                            {password.length > 0 && (
                                <div className="bg-gray-50 rounded-2xl p-4 space-y-2">
                                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                                        Password Requirements
                                    </p>
                                    <Requirement passed={checks.length} label={`At least ${PASSWORD_MIN_LENGTH} characters`} />
                                    <Requirement passed={checks.uppercase} label="One uppercase letter (A–Z)" />
                                    <Requirement passed={checks.lowercase} label="One lowercase letter (a–z)" />
                                    <Requirement passed={checks.digit} label="One number (0–9)" />
                                    <Requirement passed={checks.special} label='One special character (!@#$%^&* etc.)' />
                                </div>
                            )}

                            {/* Confirm match indicator */}
                            {confirmPassword.length > 0 && password !== confirmPassword && (
                                <p className="text-xs text-red-600 flex items-center gap-1.5">
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Passwords do not match yet.
                                </p>
                            )}

                            <button
                                type="submit"
                                disabled={loading || !passwordIsValid || password !== confirmPassword}
                                className="relative w-full group overflow-hidden bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3.5 rounded-2xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform enabled:hover:scale-[1.02] active:scale-[0.98]"
                            >
                                <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></span>
                                <span className="relative flex items-center justify-center gap-3 text-lg">
                                    {loading ? (
                                        <>
                                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                            </svg>
                                            Resetting Password...
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                            </svg>
                                            Reset Password
                                            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </>
                                    )}
                                </span>
                            </button>

                            <Link
                                to="/login"
                                className="block text-center text-sm text-[#3a663f] hover:text-[#2d5232] font-medium transition-colors"
                            >
                                ← Back to Login
                            </Link>
                        </form>
                    ) : (
                        // ─── Invalid / expired token ───────────────
                        <div className="text-center space-y-4">
                            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100">
                                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-800">Invalid Reset Link</h3>
                                <p className="text-sm text-gray-500 mt-2">
                                    {error || 'The password reset link is invalid or has expired.'}
                                </p>
                            </div>
                            <Link
                                to="/forgot-password"
                                className="block w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-3 rounded-2xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 text-center"
                            >
                                Request New Reset Link
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────
// Sub-component — password requirement row
// ─────────────────────────────────────────────────────────────
function Requirement({ passed, label }) {
    return (
        <div className={`flex items-center gap-2 text-xs transition-colors ${passed ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${passed ? 'bg-green-100' : 'bg-gray-200'}`}>
                {passed ? (
                    <svg className="w-2.5 h-2.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                ) : (
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                )}
            </div>
            <span>{label}</span>
        </div>
    );
}